import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Badge, Clickable, Sub, Table } from '../../performance-operations/styles';
import Button from '../../../components/ui/Button';
import { fmt, hasNum, seriesRows } from '../config';

// Analiz §14 E11 orta sol — dönem bazlı puan ve hedef çizgileri; seri başına kapsama n ve ölçüm ölçeği etiketi. Ölçekler farklı
// ve İK eşleme onayı yoksa final çizgisi ÇİZİLMEZ (karşılaştırma durdurulur, neden gösterilir); hedef puanı her dönemde 0–100
// olduğundan çizilir. Veri noktası → dönemin çalışan listesi (E03 bağlandığında E03). "Tablo Olarak Göster" + klavye erişimi.
const Notice = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border-left: 3px solid ${({ theme }) => theme.colors.auxiliar.warning};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const Labels = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

function valueCell(v, t, lang) {
  if (v?.suppressionReason) return t('performance.trends.suppressed');
  if (v?.unavailableReason) return t(`performance.trends.reason.${v.unavailableReason}`, { defaultValue: t('performance.trends.reason.noData') });
  return hasNum(v?.value) ? fmt(v.value, lang) : t('performance.operations.kpi.noData');
}

export default function TrendSeriesPanel({ panel, onPoint, onScaleMapping, canManageScales }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const data = panel.data;
  const comparison = data?.scaleComparison;
  const basis = comparison?.comparable ? comparison.basis : null;
  const rows = seriesRows(data?.points, basis);
  const empty = rows.length === 0;
  const finalLabel = basis === 'normalized' ? t('performance.trends.series.normalized') : t('performance.trends.series.final');

  return (
    <PanelCard
      panelKey="trendSeries" title={t('performance.trends.panel.series')} span={7}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={empty}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {comparison && !comparison.comparable ? (
        <Notice data-notice="scaleMismatch">
          <span>{t('performance.trends.scaleMismatchNotice', { count: comparison.missingPairs.length })}</span>
          {canManageScales ? <Button variant="outline" onClick={onScaleMapping} data-action="scale-mapping">{t('performance.trends.scaleMapping.open')}</Button> : null}
        </Notice>
      ) : null}
      {comparison?.basis === 'normalized' ? <Notice data-notice="normalized">{t('performance.trends.normalizedNotice')}</Notice> : null}
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.trends.col.period')}</th>
              <th>{t('performance.trends.col.scale')}</th>
              <th>{t('performance.trends.col.n')}</th>
              <th>{t('performance.trends.series.final')}</th>
              <th>{t('performance.trends.series.normalized')}</th>
              <th>{t('performance.trends.series.goal')}</th>
              <th>{t('performance.trends.series.competency')}</th>
              <th>{t('performance.trends.col.processCompletion')}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.points || []).map((p) => (
              <tr key={p.period.periodId} data-row={p.period.periodId}>
                <td>
                  <Clickable onClick={() => onPoint?.(p.period.periodId)}>{p.period.name}</Clickable>
                </td>
                <td>{p.period.scaleKey === 'goal100' ? t('performance.trends.goalScale') : (p.period.scaleName || '-')}</td>
                <td>{hasNum(p.finalN) ? `${p.finalN} / ${p.eligibleN}` : t('performance.trends.suppressed')}</td>
                <td>{valueCell(p.final, t, lang)}</td>
                <td>{valueCell(p.normalized, t, lang)}</td>
                <td>{valueCell(p.goal, t, lang)}</td>
                <td>{valueCell(p.competency, t, lang)}</td>
                <td>{valueCell(p.processCompletion, t, lang)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <LineChart
                data={rows}
                margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                style={{ cursor: 'pointer' }}
                onClick={(state) => {
                  const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
                  if (Number.isInteger(index) && rows[index]) onPoint?.(rows[index].key);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: theme.metrics.font.size.xs }} interval="preserveStartEnd" />
                <YAxis yAxisId="score" tick={{ fontSize: theme.metrics.font.size.xs }} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: theme.metrics.font.size.xs }} />
                <Tooltip formatter={(v) => fmt(v, lang)} />
                <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
                {basis ? (
                  <Line
                    yAxisId={basis === 'normalized' ? 'pct' : 'score'} type="monotone" dataKey="final" name={finalLabel}
                    stroke={theme.colors.primary.main} strokeWidth={2} connectNulls={false} dot={{ r: 4 }}
                  />
                ) : null}
                <Line yAxisId="pct" type="monotone" dataKey="goal" name={t('performance.trends.series.goal')}
                  stroke={theme.colors.secondary.main} strokeWidth={2} connectNulls={false} />
                <Line yAxisId="pct" type="monotone" dataKey="competency" name={t('performance.trends.series.competency')}
                  stroke={theme.colors.auxiliar.purple} strokeWidth={2} strokeDasharray="4 3" connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Labels>
            {rows.map((r) => {
              const p = (data?.points || []).find((x) => x.period.periodId === r.key);
              return (
                <Badge key={r.key} data-label={r.key} $tone={r.imported ? 'info' : undefined}>
                  {`${r.name} · ${hasNum(r.n) ? `n=${r.n}` : t('performance.trends.suppressed')} · ${p?.period.scaleKey === 'goal100' ? t('performance.trends.goalScale') : (p?.period.scaleName || '-')}`}
                  {r.imported ? ` · ${t('performance.trends.imported')}` : ''}
                  {!r.closed ? ` · ${t('performance.trends.notClosed')}` : ''}
                </Badge>
              );
            })}
          </Labels>
        </>
      )}
      <Sub style={{ marginTop: 8 }}>{t(`performance.trends.modeNote.${data?.mode || 'cohort'}`, { n: data?.cohortN ?? 0 })}</Sub>
    </PanelCard>
  );
}
